import { createSelector } from "@reduxjs/toolkit"
import { useMemo } from "react"
import { useSelector, useDispatch } from "react-redux"

import useCollection from "areas/collection/hoc/use-collection"
import {
  clearCategoryFilters,
  setPendingAnnouncement,
  toggleFilterModal,
  toggleFilterPanel,
  updateFilter,
  updateUniqueFilter,
  updateCurrentSort,
} from "areas/collection/store/actions"

const selectCollectionProps = createSelector(
  [(state) => state.collection],
  (collection) => {
    const {
      applied_filters = [],
      loading,
      filters,
      openedPanel,
      sort,
    } = collection
    return {
      appliedFilters: applied_filters,
      currentSort: sort,
      filters,
      hasFilters: filters.some((filter) =>
        filter.categories.some((cat) => cat.is_active_filter)
      ),
      loading,
      openedPanel,
      tags: filters.reduce(
        (tags, filter) => [
          ...tags,
          ...filter.categories.filter((cat) => cat.is_active_filter),
        ],
        []
      ),
    }
  }
)

export default function withFilters(WrappedComponent) {
  const WithFilters = (props) => {
    const dispatch = useDispatch()
    const { data } = useCollection()

    const {
      appliedFilters,
      currentSort,
      filters,
      hasFilters,
      loading,
      openedPanel,
      tags,
    } = useSelector(selectCollectionProps)

    const sortMenu = data?.sort_menu || {}
    const currentSortOption = useMemo(() => {
      const options = data?.sort_menu?.options || []
      return options.find((opt) => opt.sort === currentSort) ?? options?.[0]
    }, [currentSort, data])

    const collectionProps = {
      appliedFilters,
      collectionFilters: filters,
      currentSort,
      currentSortOption,
      // disableFilters: data?.filter_menu?.disable_filters,
      // disableSort: sortMenu?.disable_sort,
      hasFilters,
      loading,
      openedPanel,
      sortMenu,
      tags,
    }

    const dispatchProps = {
      clearAllTags: (e) => {
        e.preventDefault()
        dispatch(setPendingAnnouncement("filters_cleared"))
        dispatch(clearCategoryFilters("all"))
      },
      removeFilter: (e, filter) => {
        e.preventDefault()
        dispatch(
          setPendingAnnouncement(`filter_removed:${filter.name ?? filter}`)
        )
        dispatch(updateFilter(filter))
      },
      setFilter: (filter, unique, belongs_to) => {
        // Find the category name for the announcement
        const categoryName = filters
          .flatMap((f) => f.categories)
          .find((cat) =>
            Array.isArray(filter)
              ? filter.includes(cat.filter_param?.[0])
              : cat.filter_param?.includes(filter)
          )?.name
        const isCurrentlyActive = filters
          .flatMap((f) => f.categories)
          .find((cat) =>
            Array.isArray(filter)
              ? filter.includes(cat.filter_param?.[0])
              : cat.filter_param?.includes(filter)
          )?.is_active_filter
        const action = isCurrentlyActive ? "filter_removed" : "filter_applied"
        dispatch(setPendingAnnouncement(`${action}:${categoryName ?? filter}`))
        if (unique) {
          dispatch(updateUniqueFilter({ belongs_to, filter }))
        } else {
          dispatch(updateFilter(filter))
        }
      },
      toggleFilterModal: (show) => dispatch(toggleFilterModal(show)),
      toggleFilterPanel: (panel) => dispatch(toggleFilterPanel(panel)),
      updateSort: (option) => {
        const sortLabel =
          sortMenu?.options?.find((o) => o.sort === option)?.label ?? option
        dispatch(setPendingAnnouncement(`sort:${sortLabel}`))
        dispatch(updateCurrentSort(option))
      },
    }

    return (
      <WrappedComponent {...collectionProps} {...dispatchProps} {...props} />
    )
  }

  return WithFilters
}
